/**
 * Translated status labels.
 *
 * `L('role', user.role)` → `{ label: 'Tecnico', tone: 'neutral' }`.
 *
 * An unknown key returns the raw code rather than an empty badge: a status the backend
 * has and the portal does not know about should be visible and obviously odd, not
 * silently blank.
 */

import { useCallback } from 'react';

import { useI18n, type TranslationKey } from '@/i18n';
import { toneFor, type Label, type LabelGroup } from './labels';

export type LabelFn = (group: LabelGroup, key: string) => Label;

export function useLabel(): LabelFn {
  const { t } = useI18n();

  return useCallback(
    (group, key) => {
      const dictKey = `status.${group}.${key}` as TranslationKey;
      const translated = t(dictKey);
      return {
        // `t` returns the key path when it finds nothing; fall back to the raw code,
        // which at least tells whoever is looking what the backend actually sent.
        label: translated === dictKey ? key : translated,
        tone: toneFor(group, key),
      };
    },
    [t],
  );
}

/**
 * Alert kinds.
 *
 * Its own hook rather than a `status.` group, because an alert kind carries no tone —
 * the severity does that, and giving the kind a colour too would be two signals
 * competing to say the same thing.
 */
export function useAlertKind(): (kind: string) => string {
  const { t } = useI18n();

  return useCallback(
    (kind) => {
      const key = `status.alertKind.${kind}` as TranslationKey;
      const translated = t(key);
      return translated === key ? kind : translated;
    },
    [t],
  );
}

/**
 * Audit actions.
 *
 * Separate from the status groups because the keys are dotted (`shift.clock_in`), which
 * would collide with the dictionary's own path separator. Kept as a flat map keyed by
 * the backend's action string.
 */
export function useAuditAction(): (action: string) => string {
  const { t } = useI18n();

  return useCallback(
    (action) => {
      const key = `audit.actions.${action.replace('.', '_')}` as TranslationKey;
      const translated = t(key);
      return translated === key ? action : translated;
    },
    [t],
  );
}
